// Widget registry: the single source of truth mapping a widget id to its
// React component plus display metadata. Add a new widget here and it becomes
// assignable to any panel via the dashboard config.
//
// Entries are sorted alphabetically by id.

import AdvancedFlowsDashboard from '../0_DTE_Advanced_Flows_Dashboard';
import { BookmapLightweightPrimitives } from '../Widgets/BookmapLightweight';
import CondensedChainTable from '../Widgets/CondensedChainTable';
import EodSettlementMonitor from '../Widgets/EODSettlementMonitor';
import GexPhaseDeltaFlow from '../Widgets/GexPhaseDeltaFlow';
import GexTrendMonitor from '../Widgets/GexTrendMonitor';
import MarketIntelligenceFeedWidget from '../Widgets/MarketIntelligenceFeedWidget';
import DeltaFlowMeter from '../Widgets/DeltaFlowMeter';

export const WIDGET_REGISTRY = {
  chart: {
    id: 'chart',
    label: 'Price Chart',
    category: 'Chart',
    component: AdvancedFlowsDashboard,
    bare: true,
    replayCoverage: 'partial',
    replaySources: {
      required: ['index:SPX'],
      optional: ['chain', 'greeks:exposures', 'greeks:gexTrend', 'greeks:dynamicLevels', 'greeks:rankedOptions'],
    },
  },

  condensedChain: {
    id: 'condensedChain',
    label: 'Option Chain',
    category: 'Flow',
    component: CondensedChainTable,
    replayCoverage: 'partial',
    replaySources: { required: ['chain'] },
  },

  deltaFlowMeter: {
    id: 'deltaFlowMeter',
    label: 'Delta Flow Meter',
    category: 'Flow',
    component: DeltaFlowMeter,
    replayCoverage: 'partial',
    replaySources: { required: ['chain'] },
  },

  eodRangeInsights: {
    id: 'eodRangeInsights',
    label: 'EOD Range Insights',
    category: 'Flow',
    component: EodSettlementMonitor,
    replayCoverage: 'partial',
    replaySources: { required: ['index:SPX'], optional: ['mi', 'greeks:gexTrend'] },
  },

  flowmap: {
    id: 'flowmap',
    label: 'Flowmap',
    category: 'Flow',
    component: BookmapLightweightPrimitives,
    replayCoverage: 'partial',
    replaySources: { required: ['chain'], optional: ['index:SPX', 'greeks:exposures'] },
  },

  gexPhaseDeltaFlow: {
    id: 'gexPhaseDeltaFlow',
    label: 'GEX Phase Delta Flow',
    category: 'Flow',
    component: GexPhaseDeltaFlow,
    replaySources: { required: ['greeks:exposures'] },
  },

  gexTrendMonitor: {
    id: 'gexTrendMonitor',
    label: 'GEX Trend Monitor',
    category: 'Flow',
    component: GexTrendMonitor,
    replaySources: { required: ['greeks:gexTrend'], optional: ['greeks:exposures'] },
  },
  miFeed: {
    id: 'miFeed',
    label: 'Market Intelligence',
    category: 'Analysis',
    component: MarketIntelligenceFeedWidget,
    replaySources: { required: ['mi'] },
  },

  // optionChain: {
  //   id: 'optionChain',
  //   label: 'Option Chain',
  //   tabLabel: 'Chain',
  //   category: 'Flow',
  //   component: OptionChainTable,
  //   replayCoverage: 'partial',
  //   replaySources: { required: ['chain'] },
  // },
  // zeroGexPhase: {
  //   id: 'zeroGexPhase',
  //   label: '0-GEX Phase',
  //   category: 'Flow',
  //   component: ZeroGexPhase,
  //   replaySources: { required: ['greeks:exposures'] },
  // },
};

// Widgets whose replay history only reaches back to when THIS browser connected,
// rather than to the session open. They read chainSnaps (sampled locally from
// rowsBySym) or indexData (localStorage dailyCache only) -- neither of which a
// late join can backfill until TD026's chain-snapshot service is deployed. The
// greeks-driven widgets are not listed: greekExposures and gexTrendStrength
// arrive as a full rolling 391-minute per-minute history in the WSS init frame,
// so they replay the whole session on any client.
//
// Derived from the registry rather than hardcoded in playbackStore so a new
// chain-reading widget cannot ship silently unbadged -- set replayCoverage on
// the entry and the badge follows.
export const PARTIAL_REPLAY_WIDGET_IDS = Object.values(WIDGET_REGISTRY)
  .filter((widget) => widget.replayCoverage === 'partial')
  .map((widget) => widget.id);

export function getAssignableWidgets() {
  return Object.values(WIDGET_REGISTRY);
}

export const WIDGET_LIST = getAssignableWidgets();

export function getWidget(id) {
  return WIDGET_REGISTRY[id] || null;
}
